import { TranslateService } from '@ngx-translate/core';
import {
  BatchDocumentStatus,
  BatchPopulatedDocument,
} from '../models/batch';
import { DocumentClass } from '../models/documentClass';

export type DocumentAnalysisStatusTooltip = {
  class: string;
  tooltip: string;
  label: string
};

/**
 * Maps a document's computed batch-analysis statuses to a display chip:
 * class (color), label and tooltip translation key.
 */
export function getDocumentStatus(
  document: BatchPopulatedDocument,
  translate: TranslateService,
): DocumentAnalysisStatusTooltip {
  if (document.analysis === 'NOT_ANALYZED') {
    return {
      class: 'light',
      tooltip: translate.instant('BATCH.DOCUMENT_NOT_ANALYZED_HINT'),
      label: translate.instant('BATCH.DOCUMENT_NOT_ANALYZED'),
    };
  }
  if (document.analysis === 'EXCLUDED') {
    return {
      class: 'light',
      tooltip: translate.instant('BATCH.DOCUMENT_EXCLUDED_HINT'),
      label: translate.instant('BATCH.DOCUMENT_EXCLUDED'),
    };
  }
  if (document.analysis === 'ANALYZING') {
    return {
      class: 'light',
      tooltip: translate.instant('BATCH.DOCUMENT_PENDING_HINT'),
      label: translate.instant('BATCH.DOCUMENT_ANALYZING'),
    };
  }
  if (document.status.length === 1 && document.status[0] ===
    BatchDocumentStatus.OK) {
    return {
      class: 'green',
      tooltip: translate.instant(`BATCH.DOCUMENT_${document.nonUse
        ? 'NON_USE'
        : 'OK'}_HINT`),
      label: translate.instant(`BATCH.DOCUMENT_${document.nonUse
        ? 'NON_USE'
        : 'OK'}`),
    };
  }
  if (document.status.length === 1 && document.status[0] === BatchDocumentStatus.ERROR) {
    return {
      class: 'red',
      tooltip: translate.instant(`BATCH.DOCUMENT_${document.nonUse
        ? 'NON_USE'
        : 'ERROR'}_HINT`),
      label: translate.instant(`BATCH.DOCUMENT_${document.nonUse
        ? 'NON_USE'
        : 'ERROR'}`),
    };
  }
  // prev check was just "if (document.status)" which would be truthy in case of
  // status=[]. In practice, there should always be at least one status (OK), so
  // explicitely checking for content is a non breaking change
  if (document.status && document.status.length > 0) {
    let metadata = document.status.map((s) => _statusMapper(
      s,
      translate,
    ))
      .join(', ');
    if ([
      DocumentClass.AUTOMATIC,
      DocumentClass.OTHER,
    ].includes(document.class)) {
      metadata += '& ' + translate.instant('DOCUMENT.CLASS')
        .toLowerCase();
    }
    return {
      class: 'orange',
      tooltip: translate.instant(
        `BATCH.DOCUMENT_${document.nonUse ? 'NON_USE' : 'WARNING'}_HINT`,
        { metadata },
      ),
      label: translate.instant(`BATCH.DOCUMENT_${document.nonUse
        ? 'NON_USE'
        : 'WARNING'}`),
    };
  }
  return {
    class: 'light',
    tooltip: translate.instant('BATCH.DOCUMENT_PENDING_HINT'),
    label: translate.instant('BATCH.DOCUMENT_PENDING'),
  };
}

function _statusMapper(
  status: BatchDocumentStatus, translate: TranslateService): string {
  switch (status) {
    case BatchDocumentStatus.NO_NICE_CLASSIFICATION:
      return translate.instant('DOCUMENT.NICE_CLASSIFICATION')
        .toLowerCase();
    case BatchDocumentStatus.NO_TERRITORY:
      return translate.instant('DOCUMENT.TERRITORIES')
        .toLowerCase();
    case BatchDocumentStatus.NO_DATE:
      return translate.instant('DOCUMENT.PERIOD')
        .toLowerCase();
    case BatchDocumentStatus.NO_TRADEMARK:
      return translate.instant('ENTITIES.TRADEMARKS')
        .toLowerCase();
    default:
      return '';
  }
}
